import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { adminLogin } from "../services/authService";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import omnyxLogoText from "../assets/logos/omnyx-logo-text.png";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // if (!email.endsWith("@omnyx.co.za")) {
    //   setError("Access restricted to @omnyx.co.za accounts.");
    //   return;
    // }

    setLoading(true);
    try {
      const res = await adminLogin(email, password);
      if (res.isSuccess) {
        login(res.data);
        navigate("/", { replace: true });
      } else {
        setError(res.message ?? "Invalid credentials.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ??
          "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-base flex items-center justify-center px-4 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-10"
          style={{
            background: "radial-gradient(ellipse, #00D4AA 0%, transparent 70%)",
          }}
        />
      </div>

      <div className="w-full max-w-sm z-10">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          {omnyxLogoText ? (
            <img src={omnyxLogoText} alt="Omnyx" className="h-10 mb-3" />
          ) : (
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-lg omnyx-gradient-bg flex items-center justify-center font-bold text-white text-sm">
                αx
              </div>
              <span className="text-xl font-bold omnyx-gradient-text tracking-widest">
                OMYX
              </span>
            </div>
          )}
          <p className="text-text-muted text-xs tracking-widest uppercase">
            Internal Portal
          </p>
        </div>

        {/* Card */}
        <div className="bg-bg-surface border border-border rounded-2xl p-8">
          <h1 className="text-text-primary text-xl font-semibold mb-1">
            Welcome back
          </h1>
          <p className="text-text-muted text-sm mb-7">
            Sign in to your Omnyx account
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-xs text-text-muted uppercase tracking-wider mb-2">
                Email
              </label>
              <div className="relative">
                <Mail
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-hint"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@omnyx.co.za"
                  required
                  className="w-full bg-bg-base border border-border rounded-lg pl-9 pr-4 py-2.5 text-sm text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition"
                />
              </div>
              <p className="text-xs text-text-hint mt-1.5">
                Restricted to @omnyx.co.za accounts
              </p>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs text-text-muted uppercase tracking-wider mb-2">
                Password
              </label>
              <div className="relative">
                <Lock
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-hint"
                />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••"
                  required
                  className="w-full bg-bg-base border border-border rounded-lg pl-9 pr-10 py-2.5 text-sm text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-hint hover:text-text-secondary transition"
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full omnyx-gradient-bg text-white font-semibold text-sm py-2.5 rounded-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-text-hint text-xs mt-6">
          Omnyx Internal — Not for public use
        </p>
      </div>
    </div>
  );
};

export default Login;
